import React, { ReactNode, useRef } from 'react';
import { Formik, FormikActions, FormikErrors, FormikTouched, FormikValues } from 'formik';
import { BaseFormModalTemplate, FormModalButtons } from './BaseModalTemplate';
import { ApiDataBinding } from 'react-api-data';
import { toast } from 'react-toastify';
import { Schema } from 'yup';
import { getClientErrors } from 'utils/apiUtils';
import { NetworkRequestStatus } from 'constants/literals';

interface Props<Values> {
    isOpen: boolean;
    header: string;
    initialValues: Values;
    toggle: () => void;
    children: (props: {
        errors: FormikErrors<Values>;
        touched: FormikTouched<Values>;
        values: Values;
        setValues(values: Values): void;
        setFieldValue(field: string, value: any): void;
        handleBlur: any;
    }) => ReactNode;
    onSubmit: (values: Values) => Promise<ApiDataBinding<any>>;
    validationSchema?: Schema<Partial<Values>>;
    onSuccess?: (binding: ApiDataBinding<any>) => void;
    successMessage?: string;
}

function FormModal<T extends FormikValues>(props: Props<T>) {
    async function submit(formik: FormikActions<T>, values: T) {
        const binding = await props.onSubmit(values);
        if (binding.request.networkStatus === NetworkRequestStatus.Success) {
            handleSuccess(formik, binding);
        } else if (binding.request.networkStatus === NetworkRequestStatus.Failed) {
            handleFailure(formik, binding);
        }
    }

    function handleSuccess(formik: FormikActions<T>, binding: ApiDataBinding<any>) {
        formik.setStatus('success');
        formik.setSubmitting(false);
        props.toggle();
        if (props.successMessage) {
            toast.success(props.successMessage);
        }
        if (props.onSuccess) {
            props.onSuccess(binding);
        }
    }

    function handleFailure(formik: FormikActions<T>, binding: ApiDataBinding<any>) {
        formik.setStatus('error');
        formik.setSubmitting(false);
        const errors = getClientErrors(binding.request);
        if (errors) {
            toast.error(errors);
        }
    }

    return (
        <Formik
            initialValues={props.initialValues}
            validationSchema={props.validationSchema}
            onSubmit={(values, formik) => submit(formik, values)}
        >
            {({ handleSubmit, isSubmitting, errors, touched, values, setValues, handleBlur, resetForm, setFieldValue }) => (
                <BaseFormModalTemplate
                    isOpen={props.isOpen}
                    toggle={isSubmitting ? undefined : props.toggle}
                    onClosed={resetForm}
                    handleSubmit={handleSubmit}
                    header={props.header}
                    body={props.children({ errors, touched, values, setValues, handleBlur, setFieldValue })}
                    footer={<FormModalButtons loading={isSubmitting} toggle={props.toggle} />}
                />
            )}
        </Formik>
    );
}

export default FormModal;
